import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useOfferAffiliates } from "@/hooks/use-offers";
import { StatusBadge } from "@/components/StatusBadge";
import { Plus, Trash2, DollarSign, Pencil, X, Check } from "lucide-react";
import { formatDate, cn } from "@/lib/utils";

interface CustomPayout {
  id: number;
  affiliateId: number | null;
  affiliateName: string | null;
  isApplyAllAffiliates: boolean;
  payoutType: string;
  payoutAmount: number;
  payoutPercentage: number;
  revenueType: string;
  revenueAmount: number;
  revenuePercentage: number;
  dateValidFrom: string;
  dateValidTo: string;
  status: string;
  isPostbackDisabled: boolean;
  targetCountries: string | null;
  targetPlatforms: string | null;
  targetDeviceTypes: string | null;
  variables: string;
  createdAt: string;
}

const PAYOUT_TYPES = [
  { value: "cpa", label: "CPA" },
  { value: "cpc", label: "CPC" },
  { value: "cpm", label: "CPM" },
  { value: "cps", label: "CPS" },
  { value: "cpi", label: "CPI" },
  { value: "revshare", label: "RevShare" },
];

const REVENUE_TYPES = [
  { value: "rpa", label: "RPA" },
  { value: "rpc", label: "RPC" },
  { value: "rpm", label: "RPM" },
  { value: "rps", label: "RPS" },
  { value: "rpi", label: "RPI" },
];

const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-sm font-medium text-gray-700 mb-1";

export function OfferCustomPayoutsTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "custom-payouts"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/custom-payouts`);
      if (!res.ok) throw new Error("Failed to fetch custom payouts");
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (payoutId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/custom-payouts/${payoutId}`);
      if (!res.ok) throw new Error("Failed to delete payout");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-payouts"] }),
  });

  const payouts: CustomPayout[] = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">
          {payouts.length} custom payout{payouts.length !== 1 ? "s" : ""}
        </h3>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Add Custom Payout
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : payouts.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No custom payout/revenue settings configured.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Affiliate</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Payout</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Revenue</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Date Range</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Targeting</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => {
                const countries = p.targetCountries ? JSON.parse(p.targetCountries) : [];
                const platforms = p.targetPlatforms ? JSON.parse(p.targetPlatforms) : [];
                const devices = p.targetDeviceTypes ? JSON.parse(p.targetDeviceTypes) : [];
                const hasTargeting = countries.length > 0 || platforms.length > 0 || devices.length > 0;
                return (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        {p.isApplyAllAffiliates ? "All affiliates" : p.affiliateName || `#${p.affiliateId}`}
                      </span>
                      {p.isPostbackDisabled && <span className="ml-1 text-xs text-orange-600">(PB off)</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-900">
                        {PAYOUT_TYPES.find((t) => t.value === p.payoutType)?.label || p.payoutType?.toUpperCase()}{" "}
                        {p.payoutType === "revshare" ? `${p.payoutPercentage ?? 0}%` : `$${(p.payoutAmount ?? 0).toFixed(2)}`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-gray-900">
                        {REVENUE_TYPES.find((t) => t.value === p.revenueType)?.label || p.revenueType?.toUpperCase()}{" "}
                        ${(p.revenueAmount ?? 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-600">
                        <div>{formatDate(p.dateValidFrom)}</div>
                        <div className="text-gray-400">to {formatDate(p.dateValidTo)}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {hasTargeting ? (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          {countries.length > 0 && <div>GEO: {countries.join(", ")}</div>}
                          {platforms.length > 0 && <div>OS: {platforms.join(", ")}</div>}
                          {devices.length > 0 && <div>Dev: {devices.join(", ")}</div>}
                        </div>
                      ) : <span className="text-xs text-gray-400">Any</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditingId(p.id)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => { if (window.confirm("Delete this custom payout?")) deleteMutation.mutate(p.id); }}
                          className="p-1.5 text-red-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <CustomPayoutFormModal offerId={offerId} onClose={() => setShowForm(false)} />}
      {editingId !== null && (
        <EditPayoutModal offerId={offerId} payout={payouts.find((p) => p.id === editingId)!} onClose={() => setEditingId(null)} />
      )}
    </div>
  );
}

function CustomPayoutFormModal({ offerId, onClose }: { offerId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: affData } = useOfferAffiliates(offerId);
  const assignedAffiliates = affData?.data ?? [];

  const { data: payoutEntriesData } = useQuery({
    queryKey: ["offers", offerId, "payouts"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/payouts`);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });
  const payoutEntries: Array<{ id: number; entryName: string; payoutType: string; payoutAmount: number; payoutPercentage: number; revenueType: string; revenueAmount: number }> = payoutEntriesData?.data ?? [];

  const [selectedEntryId, setSelectedEntryId] = useState("");
  const [affiliateId, setAffiliateId] = useState("");
  const [isApplyAllAffiliates, setIsApplyAllAffiliates] = useState(false);
  const [payoutType, setPayoutType] = useState("cpa");
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutPercentage, setPayoutPercentage] = useState("");
  const [revenueType, setRevenueType] = useState("rpa");
  const [revenueAmount, setRevenueAmount] = useState("");
  const [dateValidFrom, setDateValidFrom] = useState("");
  const [dateValidTo, setDateValidTo] = useState("");
  const [status, setStatus] = useState("active");
  const [isPostbackDisabled, setIsPostbackDisabled] = useState(false);
  const [targetCountries, setTargetCountries] = useState("");
  const [targetPlatforms, setTargetPlatforms] = useState("");
  const [targetDeviceTypes, setTargetDeviceTypes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  function handleSelectEntry(entryId: string) {
    setSelectedEntryId(entryId);
    if (!entryId) return;
    const entry = payoutEntries.find((e) => e.id === Number(entryId));
    if (entry) {
      setPayoutType(entry.payoutType || "cpa");
      setPayoutAmount(String(entry.payoutAmount ?? 0));
      setPayoutPercentage(String(entry.payoutPercentage ?? 0));
      setRevenueType(entry.revenueType || "rpa");
      setRevenueAmount(String(entry.revenueAmount ?? 0));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const isRevShare = payoutType === "revshare";
      const payload: Record<string, unknown> = {
        isApplyAllAffiliates,
        payoutType,
        payoutAmount: isRevShare ? 0 : Number(payoutAmount),
        payoutPercentage: isRevShare ? Number(payoutPercentage) : 0,
        revenueType,
        revenueAmount: Number(revenueAmount),
        dateValidFrom: dateValidFrom || undefined,
        dateValidTo: dateValidTo || undefined,
        status,
        isPostbackDisabled,
      };
      if (!isApplyAllAffiliates) {
        if (!affiliateId) throw new Error("Please select an affiliate or enable 'Apply to all affiliates'");
        payload.affiliateId = Number(affiliateId);
      }
      if (targetCountries.trim()) payload.targetCountries = targetCountries.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (targetPlatforms.trim()) payload.targetPlatforms = targetPlatforms.split(",").map((s: string) => s.trim()).filter(Boolean);
      if (targetDeviceTypes.trim()) payload.targetDeviceTypes = targetDeviceTypes.split(",").map((s: string) => s.trim()).filter(Boolean);

      const res = await api.post(`/v1/offers/${offerId}/custom-payouts`, payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed to create"); }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-payouts"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Custom Payout</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}

          {payoutEntries.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className={labelCls + " text-blue-900"}>Base on existing payout entry</label>
              <select value={selectedEntryId} onChange={(e) => handleSelectEntry(e.target.value)} className={inputCls + " border-blue-300"}>
                <option value="">— Start from scratch —</option>
                {payoutEntries.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.entryName} — {PAYOUT_TYPES.find((t) => t.value === entry.payoutType)?.label || entry.payoutType.toUpperCase()}{" "}
                    {entry.payoutType === "revshare" ? `${entry.payoutPercentage}%` : `$${(entry.payoutAmount ?? 0).toFixed(2)}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input type="checkbox" id="applyAll" checked={isApplyAllAffiliates} onChange={(e) => setIsApplyAllAffiliates(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900" />
            <label htmlFor="applyAll" className="text-sm font-medium text-gray-700">Apply to all affiliates</label>
          </div>
          {!isApplyAllAffiliates && (
            <div>
              <label className={labelCls}>Affiliate *</label>
              <select required={!isApplyAllAffiliates} value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)} className={inputCls}>
                <option value="">Select affiliate...</option>
                {assignedAffiliates.map((a: any) => <option key={a.affiliateId} value={a.affiliateId}>{a.affiliateName} #{a.affiliateId}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Payout Type *</label>
              <select required value={payoutType} onChange={(e) => setPayoutType(e.target.value)} className={inputCls}>
                {PAYOUT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              {payoutType === "revshare" ? (
                <><label className={labelCls}>Revenue Share % *</label>
                <input type="number" required min="0" max="100" step="0.1" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} className={inputCls} /></>
              ) : (
                <><label className={labelCls}>Payout Amount *</label>
                <input type="number" required min="0" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className={inputCls} /></>
              )}
            </div>
            <div>
              <label className={labelCls}>Revenue Type *</label>
              <select required value={revenueType} onChange={(e) => setRevenueType(e.target.value)} className={inputCls}>
                {REVENUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Revenue Amount *</label>
              <input type="number" required min="0" step="0.01" value={revenueAmount} onChange={(e) => setRevenueAmount(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Valid From</label>
              <input type="date" value={dateValidFrom} onChange={(e) => setDateValidFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Valid To</label>
              <input type="date" value={dateValidTo} onChange={(e) => setDateValidTo(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Targeting (optional)</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Countries</label>
                <input type="text" value={targetCountries} onChange={(e) => setTargetCountries(e.target.value)} className={inputCls} placeholder="US,CA,GB" />
              </div>
              <div>
                <label className={labelCls}>Platforms</label>
                <input type="text" value={targetPlatforms} onChange={(e) => setTargetPlatforms(e.target.value)} className={inputCls} placeholder="iOS,Android" />
              </div>
              <div>
                <label className={labelCls}>Devices</label>
                <input type="text" value={targetDeviceTypes} onChange={(e) => setTargetDeviceTypes(e.target.value)} className={inputCls} placeholder="mobile,desktop" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status *</label>
              <select required value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={isPostbackDisabled} onChange={(e) => setIsPostbackDisabled(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900" />
                Disable Postback
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPayoutModal({ offerId, payout, onClose }: { offerId: string; payout: CustomPayout; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [payoutType, setPayoutType] = useState(payout.payoutType);
  const [payoutAmount, setPayoutAmount] = useState(String(payout.payoutAmount));
  const [payoutPercentage, setPayoutPercentage] = useState(String(payout.payoutPercentage));
  const [revenueType, setRevenueType] = useState(payout.revenueType);
  const [revenueAmount, setRevenueAmount] = useState(String(payout.revenueAmount));
  const [revenuePercentage, setRevenuePercentage] = useState(String(payout.revenuePercentage));
  const [dateValidFrom, setDateValidFrom] = useState(payout.dateValidFrom ? new Date(payout.dateValidFrom).toISOString().split("T")[0] : "");
  const [dateValidTo, setDateValidTo] = useState(payout.dateValidTo ? new Date(payout.dateValidTo).toISOString().split("T")[0] : "");
  const [status, setStatus] = useState(payout.status);
  const [isPostbackDisabled, setIsPostbackDisabled] = useState(payout.isPostbackDisabled ?? false);
  const [targetCountries, setTargetCountries] = useState(() => {
    try { return payout.targetCountries ? JSON.parse(payout.targetCountries).join(", ") : ""; } catch { return ""; }
  });
  const [targetPlatforms, setTargetPlatforms] = useState(() => {
    try { return payout.targetPlatforms ? JSON.parse(payout.targetPlatforms).join(", ") : ""; } catch { return ""; }
  });
  const [targetDeviceTypes, setTargetDeviceTypes] = useState(() => {
    try { return payout.targetDeviceTypes ? JSON.parse(payout.targetDeviceTypes).join(", ") : ""; } catch { return ""; }
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const isRevShare = payoutType === "revshare";
      const payload: Record<string, unknown> = {
        payoutType,
        payoutAmount: isRevShare ? 0 : Number(payoutAmount),
        payoutPercentage: isRevShare ? Number(payoutPercentage) : 0,
        revenueType,
        revenueAmount: Number(revenueAmount),
        revenuePercentage: Number(revenuePercentage),
        dateValidFrom: dateValidFrom || null,
        dateValidTo: dateValidTo || null,
        status,
        isPostbackDisabled,
        targetCountries: targetCountries.trim() ? targetCountries.split(",").map((s: string) => s.trim()).filter(Boolean) : null,
        targetPlatforms: targetPlatforms.trim() ? targetPlatforms.split(",").map((s: string) => s.trim()).filter(Boolean) : null,
        targetDeviceTypes: targetDeviceTypes.trim() ? targetDeviceTypes.split(",").map((s: string) => s.trim()).filter(Boolean) : null,
      };
      const res = await api.patch(`/v1/offers/${offerId}/custom-payouts/${payout.id}`, payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed to update"); }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "custom-payouts"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Edit Custom Payout</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Payout Type</label>
              <select value={payoutType} onChange={(e) => setPayoutType(e.target.value)} className={inputCls}>
                {PAYOUT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              {payoutType === "revshare" ? (
                <><label className={labelCls}>Revenue Share %</label>
                <input type="number" min="0" max="100" step="0.1" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} className={inputCls} /></>
              ) : (
                <><label className={labelCls}>Payout Amount</label>
                <input type="number" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className={inputCls} /></>
              )}
            </div>
            <div>
              <label className={labelCls}>Revenue Type</label>
              <select value={revenueType} onChange={(e) => setRevenueType(e.target.value)} className={inputCls}>
                {REVENUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Revenue Amount</label>
              <input type="number" step="0.01" value={revenueAmount} onChange={(e) => setRevenueAmount(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Valid From</label>
              <input type="date" value={dateValidFrom} onChange={(e) => setDateValidFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Valid To</label>
              <input type="date" value={dateValidTo} onChange={(e) => setDateValidTo(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Targeting</h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Countries</label>
                <input type="text" value={targetCountries} onChange={(e) => setTargetCountries(e.target.value)} className={inputCls} placeholder="US,CA,GB" />
              </div>
              <div>
                <label className={labelCls}>Platforms</label>
                <input type="text" value={targetPlatforms} onChange={(e) => setTargetPlatforms(e.target.value)} className={inputCls} placeholder="iOS,Android" />
              </div>
              <div>
                <label className={labelCls}>Devices</label>
                <input type="text" value={targetDeviceTypes} onChange={(e) => setTargetDeviceTypes(e.target.value)} className={inputCls} placeholder="mobile,desktop" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={isPostbackDisabled} onChange={(e) => setIsPostbackDisabled(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900" />
                Disable Postback
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
